/**
 * UnclaimedInterest — hak akwizycyjny dla NIEPRZEJĘTYCH profili (hodowla / firma).
 *
 * Zamiast martwego formularza pokazuje:
 *  - dowód społeczny „X osób chciało się skontaktować w tym tygodniu",
 *  - przycisk „Chcę się skontaktować" (rejestruje zainteresowanie — bez treści/danych),
 *  - CTA „To Twój profil? Przejmij i odbieraj zapytania".
 *
 * Backend: POST /api/public/interest, GET /api/public/interest/{entity_type}/{slug}
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { Bell, ShieldCheck, Loader2, Check, ArrowUp } from 'lucide-react';
import { API } from '@/utils/api';

export default function UnclaimedInterest({ entityType, slug, kind = 'firma', onClaim }) {
  const [count, setCount] = useState(0);
  const [state, setState] = useState('idle'); // idle | sending | done

  const isKennel = kind === 'hodowla';
  const nounGen = isKennel ? 'hodowli' : 'firmy';   // dopełniacz
  const nounIns = isKennel ? 'hodowlą' : 'firmą';   // narzędnik
  const nounNom = isKennel ? 'hodowla' : 'firma';   // mianownik

  useEffect(() => {
    let alive = true;
    if (!slug) return;
    axios.get(`${API}/public/interest/${entityType}/${slug}`)
      .then((r) => { if (alive) setCount(r.data?.count_7d || 0); })
      .catch(() => {});
    return () => { alive = false; };
  }, [entityType, slug]);

  const register = async () => {
    if (state !== 'idle') return;
    setState('sending');
    try {
      const r = await axios.post(`${API}/public/interest`, { entity_type: entityType, slug });
      setCount(r.data?.count_7d ?? count + 1);
      setState('done');
    } catch {
      setState('idle');
    }
  };

  const claim = () => {
    if (onClaim) return onClaim();
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div data-testid="unclaimed-interest" className="rounded-2xl border border-white/10 bg-white/[0.04] p-5">
      {count > 0 && (
        <div
          data-testid="unclaimed-interest-count"
          className="mb-3 inline-flex items-center gap-2 rounded-full bg-orange-500/15 border border-orange-400/30 px-3 py-1.5 text-sm font-semibold text-orange-200"
        >
          <Bell className="w-4 h-4" />
          {count} {count === 1 ? 'osoba chciała' : 'osób chciało'} się skontaktować w tym tygodniu
        </div>
      )}

      <p className="text-sm text-white/70 leading-relaxed mb-4">
        To nieoficjalna wizytówka {nounGen} utworzona na podstawie publicznie dostępnych danych.
        Zapytania są nieaktywne do czasu przejęcia profilu przez właściciela.
      </p>

      {state === 'done' ? (
        <div data-testid="unclaimed-interest-done" className="flex items-start gap-2 text-sm font-medium text-emerald-300">
          <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
          Dziękujemy! Zaznaczyliśmy zainteresowanie — właściciel zobaczy je, gdy przejmie profil.
        </div>
      ) : (
        <button
          type="button"
          onClick={register}
          disabled={state === 'sending'}
          data-testid="unclaimed-interest-btn"
          className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 px-4 h-10 text-sm font-semibold text-white transition disabled:opacity-50"
        >
          {state === 'sending' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
          Chcę się skontaktować z tą {nounIns}
        </button>
      )}

      <div className="mt-5 pt-4 border-t border-white/10">
        <button
          type="button"
          onClick={claim}
          data-testid="unclaimed-interest-claim"
          className="inline-flex items-center gap-2 rounded-full bg-orange-500 hover:bg-orange-400 px-4 h-10 text-sm font-bold text-white transition"
        >
          <ShieldCheck className="w-4 h-4" />
          To Twoja {nounNom}? Przejmij i odbieraj zapytania
        </button>
        <p className="mt-2 text-xs text-white/45 inline-flex items-center gap-1">
          <ArrowUp className="w-3 h-3" /> Przejęcie jest darmowe — użyj też przycisków na górze strony.
        </p>
      </div>
    </div>
  );
}
